/** Single credit pack card with Buy button */

import { Sparkles } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { CreditPack } from '../../hooks/use-credits'

interface Props {
  pack: CreditPack
  isPopular?: boolean
  isLoading: boolean
  isDark: boolean
  onBuy: (packId: string) => void
}

export function CreditPackCard({ pack, isPopular, isLoading, isDark, onBuy }: Props) {
  const totalCredits = pack.credits + pack.bonusCredits
  // Show whole-dollar packs without trailing .00; preserve cents for fractional prices
  const priceDollars = pack.priceCents / 100
  const priceUsd = priceDollars % 1 === 0 ? priceDollars.toFixed(0) : priceDollars.toFixed(2)
  const hasBonus = pack.bonusCredits > 0

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-xl border p-5',
        isPopular
          ? isDark
            ? 'border-brand-500/60 bg-surface-1 ring-1 ring-brand-500/30'
            : 'border-brand-500 bg-white ring-1 ring-brand-500/20'
          : isDark
            ? 'border-white/[0.06] bg-surface-1'
            : 'border-neutral-200 bg-white',
      )}
    >
      {isPopular && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-brand-600 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
          Most Popular
        </div>
      )}

      <h3 className={cn('text-sm font-semibold', isDark ? 'text-neutral-100' : 'text-neutral-900')}>
        {pack.name}
      </h3>

      <div className="mt-3 flex items-baseline gap-1">
        <span className={cn('text-3xl font-bold', isDark ? 'text-neutral-100' : 'text-neutral-900')}>
          ${priceUsd}
        </span>
        <span className={cn('text-xs', isDark ? 'text-neutral-500' : 'text-neutral-400')}>USD</span>
      </div>

      <div className="mt-3 space-y-1">
        <div className={cn('text-sm font-medium', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
          {totalCredits.toLocaleString()} credits
        </div>
        {hasBonus && (
          <div className="flex items-center gap-1 text-xs text-emerald-500">
            <Sparkles className="h-3 w-3" />
            +{pack.bonusCredits.toLocaleString()} bonus
          </div>
        )}
      </div>

      <button
        onClick={() => onBuy(pack.id)}
        disabled={isLoading}
        className={cn(
          'mt-5 w-full cursor-pointer rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50',
          isPopular
            ? 'bg-brand-600 text-white hover:bg-brand-700'
            : isDark
              ? 'bg-surface-3 text-neutral-200 hover:bg-surface-4'
              : 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200',
        )}
      >
        {isLoading ? 'Redirecting...' : 'Buy now'}
      </button>
    </div>
  )
}
